import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Bot, ChevronRight, ChevronLeft, CheckCircle, Loader2 } from 'lucide-react';
import { useWorkflowBlueprint } from '@/hooks/useWorkflowBlueprint';
import { IndustryDataSourceStep } from './IndustryDataSourceStep';
import { CleaningRulesStep } from './CleaningRulesStep';
import { PredictionGoalsStep } from './PredictionGoalsStep';
import { DashboardPrefsStep } from './DashboardPrefsStep';
import { SetupReviewStep } from './SetupReviewStep';
import { toast } from 'sonner';

const STEPS = [
  { id: 'industry', label: 'Your business' },
  { id: 'cleaning', label: 'Tidying up' },
  { id: 'predictions', label: 'What to predict' },
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'review', label: 'Check & save' },
];

interface Props {
  onComplete: () => void;
}

export const WorkflowSetupWizard = ({ onComplete }: Props) => {
  const { blueprint, saveBlueprint, isSaving, currentStep: savedStep } = useWorkflowBlueprint();

  const [step, setStep] = useState(0);
  const [localState, setLocalState] = useState({
    industry: '' as string,
    data_source_type: '' as string,
    cleaning_rules: {} as any,
    prediction_goals: [] as string[],
    dashboard_config: {} as any,
  });

  // Seed from existing blueprint
  useEffect(() => {
    if (blueprint) {
      setLocalState({
        industry: blueprint.industry || '',
        data_source_type: blueprint.data_source_type || '',
        cleaning_rules: blueprint.cleaning_rules || {},
        prediction_goals: (blueprint.prediction_goals as string[]) || [],
        dashboard_config: blueprint.dashboard_config || {},
      });
      // Resume at the first incomplete step
      setStep(Math.min(savedStep, 4));
    }
  }, [blueprint]);

  const progress = ((step + 1) / STEPS.length) * 100;

  const canProceed = (): boolean => {
    switch (step) {
      case 0: return !!localState.industry && !!localState.data_source_type;
      case 1: return true; // cleaning rules are optional
      case 2: return localState.prediction_goals.length > 0;
      case 3: return !!localState.dashboard_config.type && (localState.dashboard_config.metrics?.length || 0) > 0;
      case 4: return true;
      default: return false;
    }
  };

  const handleNext = async () => {
    // Auto-save progress at each step
    await saveBlueprint({
      ...localState,
      status: 'draft',
    });

    if (step < STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const handleFinish = async () => {
    await saveBlueprint({
      ...localState,
      status: 'complete',
    });
    toast.success('Workflow blueprint saved! You can now run automated workflows.');
    onComplete();
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Bot className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-xl font-semibold">Set up in a few steps</h2>
          <p className="text-sm text-muted-foreground">
            Tell us a bit about your business and data, and we'll handle the rest.
          </p>
        </div>
        <Badge variant="secondary" className="ml-auto">Required</Badge>
      </div>

      {/* Step indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          {STEPS.map((s, i) => (
            <div
              key={s.id}
              className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
                i <= step ? 'text-primary' : 'text-muted-foreground'
              } ${i < step ? 'cursor-pointer' : ''}`}
              onClick={() => i < step && setStep(i)}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs border-2 ${
                i < step ? 'bg-primary text-primary-foreground border-primary' :
                i === step ? 'border-primary text-primary' :
                'border-muted-foreground/30'
              }`}>
                {i < step ? <CheckCircle className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span className="hidden md:inline">{s.label}</span>
            </div>
          ))}
        </div>
        <Progress value={progress} className="h-1.5" />
      </div>

      {/* Step content */}
      <div className="min-h-[400px]">
        {step === 0 && (
          <IndustryDataSourceStep
            industry={localState.industry}
            dataSourceType={localState.data_source_type}
            onUpdate={(industry, dataSourceType) =>
              setLocalState(prev => ({ ...prev, industry, data_source_type: dataSourceType }))
            }
          />
        )}
        {step === 1 && (
          <CleaningRulesStep
            rules={localState.cleaning_rules}
            industry={localState.industry}
            onUpdate={(rules) => setLocalState(prev => ({ ...prev, cleaning_rules: rules }))}
          />
        )}
        {step === 2 && (
          <PredictionGoalsStep
            selectedGoals={localState.prediction_goals}
            industry={localState.industry}
            onUpdate={(goals) => setLocalState(prev => ({ ...prev, prediction_goals: goals }))}
          />
        )}
        {step === 3 && (
          <DashboardPrefsStep
            config={localState.dashboard_config}
            onUpdate={(config) => setLocalState(prev => ({ ...prev, dashboard_config: config }))}
          />
        )}
        {step === 4 && (
          <SetupReviewStep blueprint={localState} />
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t">
        <Button
          variant="outline"
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>

        {step < STEPS.length - 1 ? (
          <Button onClick={handleNext} disabled={!canProceed() || isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={handleFinish} disabled={isSaving} className="bg-primary">
            {isSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
            Save Blueprint & Start
          </Button>
        )}
      </div>
    </Card>
  );
};
